
import React, { useRef, useState } from 'react';
import { X, Camera, Upload, Loader2, AlertTriangle, CheckCircle2, ScanLine } from 'lucide-react';
import { Player } from '../types';
import { parseLineupSheet } from '../services/geminiService';

// Roman numeral → court slot
const ROMAN_TO_POS: Record<string, 'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6'> = {
  I: 'p1', II: 'p2', III: 'p3', IV: 'p4', V: 'p5', VI: 'p6',
};

// Court display layout: [back row L→R, front row L→R]
// p4(IV) | p3(III) | p2(II)   ← front row (net side)
// p5(V)  | p6(VI)  | p1(I)    ← back row (serving)
const COURT_ROWS: Array<Array<{ pos: 'p1'|'p2'|'p3'|'p4'|'p5'|'p6'; roman: string; label: string }>> = [
  [
    { pos: 'p4', roman: 'IV', label: 'LF' },
    { pos: 'p3', roman: 'III', label: 'MF' },
    { pos: 'p2', roman: 'II', label: 'RF' },
  ],
  [
    { pos: 'p5', roman: 'V', label: 'LB' },
    { pos: 'p6', roman: 'VI', label: 'MB' },
    { pos: 'p1', roman: 'I', label: 'RB' },
  ],
];

interface LineupPositions {
  p1: string; p2: string; p3: string; p4: string; p5: string; p6: string;
}

interface LineupSheetScannerProps {
  players: Player[];
  onApply: (positions: LineupPositions, liberoId: string | null) => void;
  onClose: () => void;
}

type Step = 'instructions' | 'processing' | 'review';

interface ReviewCell {
  pos: 'p1' | 'p2' | 'p3' | 'p4' | 'p5' | 'p6';
  roman: string;
  label: string;
  jersey: string;
  playerId: string;
  isCaptain: boolean;
}

function toDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const LineupSheetScanner: React.FC<LineupSheetScannerProps> = ({ players, onApply, onClose }) => {
  const [step, setStep] = useState<Step>('instructions');
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cells, setCells] = useState<ReviewCell[]>([]);
  const [liberoJersey, setLiberoJersey] = useState('');
  const [liberoPlayerId, setLiberoPlayerId] = useState('');
  const [liberoIsCaptain, setLiberoIsCaptain] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const findPlayerByJersey = (jersey: string): Player | undefined => {
    const j = jersey.trim().replace(/^#/, '').replace(/C$/i, '').trim();
    if (!j) return undefined;
    return players.find(p => p.jerseyNumber.trim() === j);
  };

  const stripCaptain = (raw: string): { jersey: string; isCaptain: boolean } => {
    const cleaned = raw.trim().replace(/^#/, '');
    const isCaptain = /C$/i.test(cleaned);
    return { jersey: cleaned.replace(/C$/i, '').trim(), isCaptain };
  };

  const processImage = async (file: File) => {
    setError(null);
    setStep('processing');
    try {
      const uri = await toDataUri(file);
      setImageUri(uri);
      const parsed = await parseLineupSheet(uri);

      // Build review cells
      const reviewCells: ReviewCell[] = COURT_ROWS.flat().map(({ pos, roman, label }) => {
        const { jersey, isCaptain } = stripCaptain(parsed[roman as keyof typeof parsed] || '');
        const player = findPlayerByJersey(jersey);
        return { pos, roman, label, jersey, playerId: player?.id || '', isCaptain };
      });
      setCells(reviewCells);

      // Libero
      const { jersey: lJersey, isCaptain: lIsCaptain } = stripCaptain(parsed.libero || '');
      const lPlayer = findPlayerByJersey(lJersey);
      setLiberoJersey(lJersey);
      setLiberoPlayerId(lPlayer?.id || '');
      setLiberoIsCaptain(lIsCaptain);

      setStep('review');
    } catch (e: any) {
      setError(e?.message || 'Failed to read lineup sheet. Please try a clearer photo.');
      setStep('instructions');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, source: 'upload' | 'camera') => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImage(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleCellJerseyChange = (pos: string, raw: string) => {
    const { jersey, isCaptain } = stripCaptain(raw);
    const player = findPlayerByJersey(jersey);
    setCells(prev => prev.map(c => c.pos === pos ? { ...c, jersey, playerId: player?.id || '', isCaptain } : c));
  };

  const handleLiberoJerseyChange = (raw: string) => {
    const { jersey, isCaptain } = stripCaptain(raw);
    setLiberoJersey(jersey);
    setLiberoIsCaptain(isCaptain);
    const player = findPlayerByJersey(jersey);
    setLiberoPlayerId(player?.id || '');
  };

  const handleApply = () => {
    const positions: LineupPositions = { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '' };
    for (const cell of cells) {
      positions[cell.pos] = cell.playerId;
    }
    onApply(positions, liberoPlayerId || null);
  };

  const unmatched = cells.filter(c => c.jersey && !c.playerId).length;
  const liberoUnmatched = liberoJersey && !liberoPlayerId;

  return (
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-screen sm:max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <ScanLine className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-slate-900 text-sm">Scan Lineup Sheet</h2>
            <p className="text-xs text-slate-500">Photo → auto-fill rotation positions</p>
          </div>
          <button onClick={onClose} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* ── Step: Instructions ── */}
          {step === 'instructions' && (
            <div className="p-5 space-y-5">
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Diagram */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">What the sheet looks like</p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  {/* Libero row */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-xs font-bold text-slate-500 w-12 shrink-0">LIBERO</div>
                    <div className="flex-1 border border-dashed border-purple-300 rounded px-2 py-1 text-center text-xs text-purple-500 font-bold bg-purple-50">
                      jersey #
                    </div>
                  </div>
                  {/* Grid */}
                  <div className="border border-slate-300 rounded overflow-hidden text-xs font-bold text-center">
                    <div className="grid grid-cols-3 divide-x divide-slate-300 border-b border-slate-300">
                      {['IV', 'III', 'II'].map(r => (
                        <div key={r} className="py-3 bg-blue-50">
                          <div className="text-[9px] text-blue-400 font-bold">{r}</div>
                          <div className="text-slate-400 text-[10px] mt-0.5">jersey #</div>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-slate-300">
                      {['V', 'VI', 'I'].map(r => (
                        <div key={r} className="py-3 bg-white">
                          <div className={`text-[9px] font-bold ${r === 'I' ? 'text-amber-500' : 'text-slate-400'}`}>{r}</div>
                          <div className="text-slate-400 text-[10px] mt-0.5">jersey #</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-center text-[9px] font-bold text-slate-400 tracking-widest uppercase mt-1">NET</div>
                </div>
              </div>

              {/* Tips */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tips for best results</p>
                {[
                  'Lay the sheet flat on a table with good lighting',
                  'Frame just the lineup card — crop out the rest of the form',
                  'Make sure jersey numbers are legible and in focus',
                ].map((tip, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-sm text-slate-600">{tip}</p>
                  </div>
                ))}
              </div>

              {/* Capture buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => cameraRef.current?.click()}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold py-3 rounded-xl transition-all"
                >
                  <Camera className="w-4 h-4" /> Take Photo
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 active:scale-95 text-slate-700 font-bold py-3 rounded-xl transition-all"
                >
                  <Upload className="w-4 h-4" /> Choose from Library
                </button>
              </div>

              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFileChange(e, 'camera')} />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleFileChange(e, 'upload')} />
            </div>
          )}

          {/* ── Step: Processing ── */}
          {step === 'processing' && (
            <div className="p-8 flex flex-col items-center justify-center gap-5 min-h-[300px]">
              {imageUri && (
                <img src={imageUri} alt="Lineup sheet" className="w-32 h-32 object-cover rounded-xl border border-slate-200 shadow" />
              )}
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <p className="font-semibold text-slate-700">Reading lineup sheet…</p>
                <p className="text-xs text-slate-400">AI is scanning the jersey numbers</p>
              </div>
            </div>
          )}

          {/* ── Step: Review ── */}
          {step === 'review' && (
            <div className="p-4 space-y-4">
              {/* Image thumbnail + retake */}
              <div className="flex items-center gap-3">
                {imageUri && (
                  <img src={imageUri} alt="Lineup sheet" className="w-14 h-14 object-cover rounded-lg border border-slate-200 shadow shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">Review detected positions</p>
                  <p className="text-xs text-slate-500">Adjust any jersey numbers that weren't read correctly</p>
                </div>
                <button
                  onClick={() => { setStep('instructions'); setError(null); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium shrink-0"
                >
                  Retake
                </button>
              </div>

              {/* Unmatched warning */}
              {(unmatched > 0 || liberoUnmatched) && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    {unmatched > 0 && `${unmatched} jersey number${unmatched > 1 ? 's' : ''} didn't match a player on your roster.`}
                    {unmatched > 0 && liberoUnmatched && ' '}
                    {liberoUnmatched && 'Libero number not matched.'}
                    {' '}Check the numbers below and correct if needed.
                  </p>
                </div>
              )}

              {/* Libero field */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
                <p className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-2">Libero</p>
                <ReviewCellInput
                  jersey={liberoJersey}
                  playerId={liberoPlayerId}
                  isCaptain={liberoIsCaptain}
                  players={players}
                  onChange={handleLiberoJerseyChange}
                  onPlayerChange={setLiberoPlayerId}
                />
              </div>

              {/* Court grid */}
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Rotation positions</p>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  {COURT_ROWS.map((row, ri) => (
                    <div key={ri} className={`grid grid-cols-3 divide-x divide-slate-200 ${ri === 0 ? 'bg-blue-50/50 border-b border-slate-200' : ''}`}>
                      {row.map(({ pos, roman, label }) => {
                        const cell = cells.find(c => c.pos === pos)!;
                        if (!cell) return null;
                        return (
                          <div key={pos} className="p-2.5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className={`text-[10px] font-black ${roman === 'I' ? 'text-amber-600' : 'text-slate-400'}`}>{roman}</span>
                              <span className="text-[9px] text-slate-400">{label}</span>
                            </div>
                            <ReviewCellInput
                              jersey={cell.jersey}
                              playerId={cell.playerId}
                              isCaptain={cell.isCaptain}
                              players={players}
                              onChange={(j) => handleCellJerseyChange(pos, j)}
                              onPlayerChange={(id) => setCells(prev => prev.map(c => c.pos === pos ? { ...c, playerId: id, jersey: players.find(p => p.id === id)?.jerseyNumber || c.jersey } : c))}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div className="text-center text-[9px] font-black text-slate-400 tracking-widest uppercase py-1 bg-slate-100 border-t border-slate-200">
                    NET
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'review' && (
          <div className="flex gap-2 p-4 border-t border-slate-100 shrink-0">
            <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl text-sm hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" /> Apply Lineup
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sub-component: editable jersey + player dropdown ─────────────────────────

interface ReviewCellInputProps {
  jersey: string;
  playerId: string;
  isCaptain?: boolean;
  players: Player[];
  onChange: (jersey: string) => void;
  onPlayerChange: (id: string) => void;
}

const ReviewCellInput: React.FC<ReviewCellInputProps> = ({ jersey, playerId, isCaptain, players, onChange, onPlayerChange }) => {
  const matched = players.find(p => p.id === playerId);

  return (
    <div className="space-y-1">
      {/* Player dropdown */}
      <select
        value={playerId}
        onChange={e => {
          const id = e.target.value;
          onPlayerChange(id);
          const p = players.find(pl => pl.id === id);
          if (p) onChange(p.jerseyNumber);
        }}
        className={`w-full text-xs font-bold border rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-400 ${
          !playerId && jersey
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : playerId
            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
            : 'border-slate-200 bg-white text-slate-500'
        }`}
      >
        <option value="">
          {jersey ? `#${jersey} — no match` : '— empty —'}
        </option>
        {players.map(p => (
          <option key={p.id} value={p.id}>#{p.jerseyNumber} {p.name}</option>
        ))}
      </select>
      {/* Scanned jersey number (editable to re-trigger matching) */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-slate-400">Scanned #</span>
        <input
          type="text"
          value={jersey}
          onChange={e => onChange(e.target.value)}
          placeholder="—"
          className="w-12 text-[11px] font-bold border border-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-400 text-center bg-white"
        />
        {isCaptain && (
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-600 text-[9px] font-black border border-amber-300 shrink-0" title="Captain">C</span>
        )}
        {matched && <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />}
        {!matched && jersey && <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />}
      </div>
    </div>
  );
};

export default LineupSheetScanner;
